import { useMemo, useState } from 'react';
import { Bevestig, Kaart, Knop, Leeg, Modaal, PaginaKop, SkeletVlak, Veld, useToast } from '../components/ui';
import { useAuth } from '../lib/auth';
import {
  useBewaarRol,
  usePermissies,
  useRolPermissies,
  useRollen,
  useVerwijderRol,
  useZetRolPermissies
} from '../lib/queries';
import type { Rol } from '../lib/types';

export default function Rollen() {
  const { demo } = useAuth();
  const rollen = useRollen();
  const permissies = usePermissies();
  const koppelingen = useRolPermissies();
  const verwijderen = useVerwijderRol();
  const meld = useToast();

  const [bewerkPermissies, setBewerkPermissies] = useState<Rol | null>(null);
  const [bewerkRol, setBewerkRol] = useState<Rol | 'nieuw' | null>(null);
  const [weg, setWeg] = useState<Rol | null>(null);

  const perRol = useMemo(() => {
    const kaart = new Map<string, { paginas: number; acties: number; sleutels: string[] }>();
    for (const r of rollen.data ?? []) kaart.set(r.sleutel, { paginas: 0, acties: 0, sleutels: [] });
    for (const k of koppelingen.data ?? []) {
      const rij = kaart.get(k.rolSleutel);
      const perm = (permissies.data ?? []).find((p) => p.sleutel === k.permissieSleutel);
      if (!rij || !perm) continue;
      rij.sleutels.push(k.permissieSleutel);
      if (perm.soort === 'pagina') rij.paginas++;
      else rij.acties++;
    }
    return kaart;
  }, [rollen.data, koppelingen.data, permissies.data]);

  if (demo) {
    return (
      <>
        <PaginaKop titel="Rollenbeheer" />
        <Kaart>
          <Leeg tekst="In demomodus zijn er geen rollen om te beheren." ikoon="schild" />
        </Kaart>
      </>
    );
  }

  const laden = rollen.isLoading || permissies.isLoading || koppelingen.isLoading;

  return (
    <>
      <PaginaKop
        titel="Rollenbeheer"
        onder="Permissies werken op twee niveaus: welke pagina's zichtbaar zijn en welke knoppen werken."
        rechts={
          <Knop variant="primair" onClick={() => setBewerkRol('nieuw')}>
            + Rol
          </Knop>
        }
      />

      {laden ? (
        <div className="grid gap-3.5 md:grid-cols-2 lg:grid-cols-3">
          <SkeletVlak hoogte="h-36" />
          <SkeletVlak hoogte="h-36" />
          <SkeletVlak hoogte="h-36" />
        </div>
      ) : (
        <div className="grid gap-3.5 md:grid-cols-2 lg:grid-cols-3">
          {(rollen.data ?? []).map((rol) => {
            const telling = perRol.get(rol.sleutel) ?? { paginas: 0, acties: 0, sleutels: [] };
            return (
              <div key={rol.sleutel} className="rand-hard flex flex-col bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="kop text-[17px]">{rol.label}</h2>
                    <p className="mt-0.5 text-xs text-krijt-500">
                      {rol.ingebouwd ? 'ingebouwd · ' : ''}
                      <span className="nums">{telling.paginas}</span> pagina's ·{' '}
                      <span className="nums">{telling.acties}</span> acties
                    </p>
                  </div>
                </div>

                {rol.omschrijving ? (
                  <p className="mt-2 text-sm leading-relaxed text-krijt-600">{rol.omschrijving}</p>
                ) : null}

                <div className="mt-4 flex flex-wrap gap-2">
                  <Knop klein onClick={() => setBewerkPermissies(rol)}>
                    Permissies bewerken
                  </Knop>
                  <Knop klein onClick={() => setBewerkRol(rol)}>
                    Naam
                  </Knop>
                  {rol.ingebouwd ? null : (
                    <Knop klein variant="gevaar" onClick={() => setWeg(rol)}>
                      Verwijderen
                    </Knop>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {bewerkPermissies ? (
        <PermissieModaal
          rol={bewerkPermissies}
          gekozen={perRol.get(bewerkPermissies.sleutel)?.sleutels ?? []}
          onSluit={() => setBewerkPermissies(null)}
        />
      ) : null}

      {bewerkRol ? (
        <RolModaal rol={bewerkRol === 'nieuw' ? null : bewerkRol} onSluit={() => setBewerkRol(null)} />
      ) : null}

      {weg ? (
        <Bevestig
          titel="Rol verwijderen?"
          tekst={`Gebruikers met de rol "${weg.label}" moeten daarna een andere rol krijgen. De rol zelf verdwijnt.`}
          knop="Verwijderen"
          onNee={() => setWeg(null)}
          onJa={async () => {
            try {
              await verwijderen.mutateAsync(weg.sleutel);
              meld('Rol verwijderd.');
            } catch (err) {
              meld(err instanceof Error ? err.message : 'Verwijderen mislukte.', true);
            }
            setWeg(null);
          }}
        />
      ) : null}
    </>
  );
}

function PermissieModaal({ rol, gekozen, onSluit }: { rol: Rol; gekozen: string[]; onSluit: () => void }) {
  const permissies = usePermissies();
  const zetten = useZetRolPermissies();
  const meld = useToast();
  const [aan, setAan] = useState<Set<string>>(new Set(gekozen));

  const paginas = (permissies.data ?? []).filter((p) => p.soort === 'pagina');
  const acties = (permissies.data ?? []).filter((p) => p.soort === 'actie');

  const wissel = (sleutel: string) =>
    setAan((oud) => {
      const nieuw = new Set(oud);
      if (nieuw.has(sleutel)) nieuw.delete(sleutel);
      else nieuw.add(sleutel);
      return nieuw;
    });

  const kolom = (titel: string, lijst: typeof paginas) => (
    <div>
      <h3 className="opschrift mb-2 text-krijt-500">{titel}</h3>
      <div className="flex flex-col gap-1.5">
        {lijst.map((p) => (
          <label key={p.sleutel} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={aan.has(p.sleutel)} onChange={() => wissel(p.sleutel)} />
            {p.label}
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <Modaal
      titel={`Permissies — ${rol.label}`}
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop
            variant="primair"
            disabled={zetten.isPending}
            onClick={async () => {
              try {
                await zetten.mutateAsync({ rol: rol.sleutel, sleutels: [...aan] });
                meld(`Permissies van ${rol.label} bijgewerkt.`);
                onSluit();
              } catch (err) {
                meld(err instanceof Error ? err.message : 'Opslaan mislukte.', true);
              }
            }}
          >
            {zetten.isPending ? 'Bezig…' : 'Opslaan'}
          </Knop>
        </>
      }
    >
      {rol.sleutel === 'admin' ? (
        <p className="mb-3 border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Let op: dit is de beheerdersrol. Haal je hier het rollenbeheer weg, dan sluit je jezelf buiten.
        </p>
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2">
        {kolom("Pagina's", paginas)}
        {kolom('Knoppen & acties', acties)}
      </div>
    </Modaal>
  );
}

function RolModaal({ rol, onSluit }: { rol: Rol | null; onSluit: () => void }) {
  const nieuw = !rol;
  const bewaren = useBewaarRol();
  const meld = useToast();
  const [sleutel, setSleutel] = useState(rol?.sleutel ?? '');
  const [label, setLabel] = useState(rol?.label ?? '');
  const [omschrijving, setOmschrijving] = useState(rol?.omschrijving ?? '');
  const [fout, setFout] = useState<string | null>(null);

  async function bewaar() {
    const s = sleutel.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    if (!label.trim()) {
      setFout('Geef de rol een naam.');
      return;
    }
    if (nieuw && !/^[a-z][a-z0-9_]{1,29}$/.test(s)) {
      setFout('De sleutel begint met een letter en bevat alleen kleine letters, cijfers en liggende streepjes.');
      return;
    }
    try {
      await bewaren.mutateAsync({
        rol: { sleutel: nieuw ? s : (rol as Rol).sleutel, label: label.trim(), omschrijving: omschrijving.trim() || null, ingebouwd: false },
        nieuw
      });
      meld(nieuw ? 'Rol aangemaakt.' : 'Rol bijgewerkt.');
      onSluit();
    } catch (err) {
      setFout(err instanceof Error ? err.message : 'Opslaan mislukte.');
    }
  }

  return (
    <Modaal
      titel={nieuw ? 'Nieuwe rol' : `Rol — ${rol?.label}`}
      onSluit={onSluit}
      voet={
        <>
          <Knop onClick={onSluit}>Annuleren</Knop>
          <Knop variant="primair" onClick={() => void bewaar()} disabled={bewaren.isPending}>
            {bewaren.isPending ? 'Bezig…' : 'Opslaan'}
          </Knop>
        </>
      }
    >
      {fout ? <div className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{fout}</div> : null}

      <Veld label="Naam">
        <input className="veld" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="bijv. Poulebeheerder" />
      </Veld>

      {nieuw ? (
        <Veld label="Sleutel" help="Zo heet de rol in de database. Kleine letters, cijfers en liggende streepjes.">
          <input className="veld" value={sleutel} onChange={(e) => setSleutel(e.target.value)} placeholder="poulebeheerder" />
        </Veld>
      ) : null}

      <Veld label="Omschrijving">
        <input className="veld" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
      </Veld>

      <p className="text-xs text-krijt-500">Nieuwe rollen beginnen zonder permissies; die zet je daarna.</p>
    </Modaal>
  );
}
